/**
 * Leaflet Guard
 * -------------
 * Monkey-patches `L.LatLng` to detect invalid coordinates (NaN / Infinity
 * / undefined / null) **before** Leaflet itself throws its opaque
 * "Invalid LatLng object: (NaN, NaN)" error.
 *
 * On detection we send a structured report via `reportClientError` that
 * includes:
 *  - the raw arguments passed to `L.LatLng`
 *  - a JS stack trace pointing to the call site inside our code
 *  - the current diagnostic context (screen + filter state) set by pages
 *
 * We deliberately keep Leaflet's original behaviour (throw the same
 * error) so the existing React error boundary still fires and the UX
 * does not change — we only add observability.
 */

import L from 'leaflet';
import { reportClientError } from './clientErrorReporter';
import { getDiagnosticContext } from './diagnosticContext';

let installed = false;

function isBadCoord(n: unknown): boolean {
  return typeof n !== 'number' || !Number.isFinite(n);
}

export function installLeafletGuard(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  const OriginalLatLng = L.LatLng;
  if (!OriginalLatLng) return;

  // Replace constructor with a wrapper that pre-validates inputs.
  function PatchedLatLng(this: unknown, lat: number, lng: number, alt?: number) {
    if (isBadCoord(lat) || isBadCoord(lng)) {
      const stack = new Error('Invalid LatLng call site').stack ?? null;
      void reportClientError({
        message: `Invalid LatLng received: (${String(lat)}, ${String(lng)})`,
        stack,
        source: 'manual',
        severity: 'fatal',
        context: {
          kind: 'leaflet_invalid_latlng',
          args: { lat: String(lat), lng: String(lng), alt: alt === undefined ? null : String(alt) },
          diagnostic: getDiagnosticContext(),
          pathname: window.location?.pathname ?? null,
          search: window.location?.search ?? null,
        },
      });
    }
    // Delegate to the original constructor — keep behaviour identical.
    return new (OriginalLatLng as unknown as new (a: number, b: number, c?: number) => unknown)(lat, lng, alt);
  }

  // Preserve prototype chain & static helpers (e.g. L.LatLng.DEG_TO_RAD).
  PatchedLatLng.prototype = OriginalLatLng.prototype;
  Object.assign(PatchedLatLng, OriginalLatLng);

  (L as unknown as { LatLng: unknown }).LatLng = PatchedLatLng;

  // L.latLng factory internally calls `new L.LatLng(...)` via the
  // reassigned reference in most builds, but some bundles capture the
  // original. Patch the factory defensively too.
  const originalFactory = L.latLng;
  (L as unknown as { latLng: typeof L.latLng }).latLng = function patchedLatLngFactory(
    a: number | L.LatLngExpression,
    b?: number,
    c?: number,
  ): L.LatLng {
    if (Array.isArray(a)) {
      if (isBadCoord(a[0]) || isBadCoord(a[1])) {
        void reportClientError({
          message: `Invalid LatLng (array) received: (${String(a[0])}, ${String(a[1])})`,
          stack: new Error('Invalid LatLng array call site').stack ?? null,
          source: 'manual',
          severity: 'fatal',
          context: {
            kind: 'leaflet_invalid_latlng',
            args: { input: a.map(String) },
            diagnostic: getDiagnosticContext(),
            pathname: window.location?.pathname ?? null,
          },
        });
      }
    } else if (typeof a === 'number' && (isBadCoord(a) || isBadCoord(b as number))) {
      void reportClientError({
        message: `Invalid LatLng (factory) received: (${String(a)}, ${String(b)})`,
        stack: new Error('Invalid LatLng factory call site').stack ?? null,
        source: 'manual',
        severity: 'fatal',
        context: {
          kind: 'leaflet_invalid_latlng',
          args: { lat: String(a), lng: String(b), alt: c === undefined ? null : String(c) },
          diagnostic: getDiagnosticContext(),
          pathname: window.location?.pathname ?? null,
        },
      });
    }
    return (originalFactory as unknown as (a: unknown, b?: unknown, c?: unknown) => L.LatLng)(a, b, c);
  };
}